import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import React from 'react';

import {DisplayInfo} from 'molbio/components/visualizations/SequenceVisualizationConstants';
import {getEnzyme} from 'molbio/enzymes/EnzymeUtil';
import {NEAR_BLACK} from 'ui-library/Colors';
import Line from 'ui-library/svg/Line';
import {Point} from 'util/geometry/GeometryConstants';

import {TextPointInfo} from './SequenceVisualizationConstants';

const MAX_BASE_LENGTH = 10;

function ellipsizeBases(bases: string): string {
  if (bases.length <= MAX_BASE_LENGTH) {
    return bases;
  }
  const numBasesOnEitherSide = MAX_BASE_LENGTH / 2 - 1;
  return `${bases.slice(0, numBasesOnEitherSide)}..${bases.slice(-numBasesOnEitherSide)}`;
}

export function getDisplayInfoText(displayInfo: DisplayInfo): string | null {
  if (displayInfo.enzymeId) {
    return getEnzyme(displayInfo.enzymeId).name;
  } else if (displayInfo.bases) {
    return ellipsizeBases(displayInfo.bases);
  } else {
    return null;
  }
}

function TextLabel({
  dataTestAttrs,
  sequencePoint,
  textPointInfo,
  text,
}: {
  dataTestAttrs?: DataTestAttrs;
  sequencePoint: Point;
  textPointInfo: TextPointInfo;
  text: string;
}): JSX.Element {
  const textPoint = textPointInfo.point;
  return (
    <g {...getTestProps(dataTestAttrs ?? {component: 'DisplayInfoLabel', element: 'root'})}>
      <Line
        dataTestAttrs={{component: dataTestAttrs?.component ?? 'DisplayInfoLabel', element: 'line'}}
        startPoint={sequencePoint}
        endPoint={textPointInfo.point}
        stroke={NEAR_BLACK}
        strokeWidth={1}
      />
      <text
        {...getTestProps({component: dataTestAttrs?.component ?? 'DisplayInfoLabel', element: 'text'})}
        textAnchor={textPointInfo.anchor}
        x={textPoint.x}
        y={textPoint.y}
        dominantBaseline="middle"
        fontSize="11px"
      >
        {text}
      </text>
    </g>
  );
}

export interface DisplayInfoLabelProps {
  dataTestAttrs?: DataTestAttrs;
  sequencePoint: Point;
  textPointInfo: TextPointInfo;
  displayInfo: DisplayInfo;
}

export default function DisplayInfoLabel({
  dataTestAttrs,
  sequencePoint,
  textPointInfo,
  displayInfo,
}: DisplayInfoLabelProps): JSX.Element | null {
  const text = getDisplayInfoText(displayInfo);
  if (!text) {
    return null;
  }
  return (
    <TextLabel
      dataTestAttrs={dataTestAttrs}
      sequencePoint={sequencePoint}
      textPointInfo={textPointInfo}
      text={text}
    />
  );
}
